import { offlineDB } from './db';
import { apiClient } from '../../api/client';

interface SyncStatus {
  isSyncing: boolean;
  pendingCount: number;
  lastSyncTime: number | null;
  errors: string[];
}

class SyncService {
  private isSyncing = false;
  private syncInterval: number | null = null;
  private listeners: ((status: SyncStatus) => void)[] = [];

  constructor() {
    // Initialize DB on service creation
    offlineDB.init().catch(console.error);

    // Listen for online/offline events
    window.addEventListener('online', () => this.triggerSync());
    window.addEventListener('offline', () => this.notifyListeners());
  }

  // Subscribe to sync status changes
  subscribe(callback: (status: SyncStatus) => void): () => void {
    this.listeners.push(callback);
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter((cb) => cb !== callback);
    };
  }

  private notifyListeners() {
    const status = this.getStatus();
    this.listeners.forEach((callback) => callback(status));
  }

  getStatus(): SyncStatus {
    return {
      isSyncing: this.isSyncing,
      pendingCount: 0, // Will be updated during sync
      lastSyncTime: this.getLastSyncTime(),
      errors: [],
    };
  }

  private getLastSyncTime(): number | null {
    const lastSync = localStorage.getItem('lastSyncTime');
    return lastSync ? parseInt(lastSync, 10) : null;
  }

  private setLastSyncTime(time: number) {
    localStorage.setItem('lastSyncTime', time.toString());
  }

  // Add order to offline queue
  async queueOrder(orderData: any): Promise<string> {
    const orderId = `offline-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    await offlineDB.addPendingOrder({
      id: orderId,
      data: orderData,
      timestamp: Date.now(),
      retryCount: 0,
    });

    this.notifyListeners();

    // Try to sync immediately if online
    if (navigator.onLine) {
      this.triggerSync();
    }

    return orderId;
  }

  // Start automatic sync (call when app loads)
  startAutoSync(intervalMs = 30000) {
    this.stopAutoSync();
    this.syncInterval = window.setInterval(() => {
      if (navigator.onLine) {
        this.triggerSync();
      }
    }, intervalMs);
  }

  stopAutoSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  // Manually trigger sync
  async triggerSync(): Promise<void> {
    if (this.isSyncing || !navigator.onLine) {
      return;
    }

    this.isSyncing = true;
    this.notifyListeners();

    try {
      const pendingOrders = await offlineDB.getPendingOrders();

      if (pendingOrders.length === 0) {
        this.isSyncing = false;
        this.notifyListeners();
        return;
      }

      console.log(`Syncing ${pendingOrders.length} pending orders...`);

      // Process orders sequentially to maintain order
      for (const order of pendingOrders) {
        try {
          // Attempt to submit order to server
          const response = await apiClient.post('/orders', order.data);

          if (response.status === 200 || response.status === 201) {
            // Success - remove from pending and add to synced
            await offlineDB.removePendingOrder(order.id);
            await offlineDB.addSyncedOrder({
              ...response.data.data.order,
              offlineId: order.id,
            });

            console.log(`Successfully synced order ${order.id}`);
          } else {
            throw new Error(`Server returned status ${response.status}`);
          }
        } catch (error) {
          console.error(`Failed to sync order ${order.id}:`, error);

          // Increment retry count
          const updatedOrder = {
            ...order,
            retryCount: order.retryCount + 1,
          };

          // If retried too many times, mark as failed
          if (updatedOrder.retryCount >= 5) {
            console.error(`Order ${order.id} failed after 5 retries, keeping in queue`);
            // Could move to a "failed" store here
          }

          await offlineDB.updatePendingOrder(updatedOrder);
        }
      }

      this.setLastSyncTime(Date.now());
    } catch (error) {
      console.error('Sync error:', error);
    } finally {
      this.isSyncing = false;
      this.notifyListeners();
    }
  }

  // Get count of pending orders
  async getPendingCount(): Promise<number> {
    const orders = await offlineDB.getPendingOrders();
    return orders.length;
  }

  // Clear all pending orders (use with caution!)
  async clearPendingOrders(): Promise<void> {
    const orders = await offlineDB.getPendingOrders();
    await Promise.all(orders.map((order) => offlineDB.removePendingOrder(order.id)));
    this.notifyListeners();
  }

  // Cleanup old data
  async cleanup(): Promise<void> {
    await offlineDB.clearExpiredCache();
    await offlineDB.clearOldSyncedOrders(7); // Keep 7 days of synced orders
  }
}

// Singleton instance
export const syncService = new SyncService();
